import React, { useState, useEffect } from "react";
import "chart.js/auto";
import { Line } from "react-chartjs-2";
import { fetchChartData, fetchStrategyTrend } from "../../../lib/researchApi";
import TrendIndicator from "../shared/TrendIndicator";

const SUPPORTED_STRATEGIES = ["wheel", "earnings", "tactical_spread", "regime_spread"];
const STRATEGY_LABELS = {
    wheel:           "Wheel",
    earnings:        "Earnings",
    tactical_spread: "Tactical Spread",
    regime_spread:   "Regime Spread",
};

const TREND_METRIC_LABELS = {
    "historical_backtest.risk_of_ruin":                "Risk of Ruin (Backtest)",
    "historical_backtest.win_rate":                    "Win Rate (Backtest)",
    "walk_forward.walk_forward_degradation":           "WF Degradation",
    "walk_forward.overfit_score":                      "Overfit Score",
    "monte_carlo_empirical.confidence_interval_lower": "CI Lower (Empirical MC)",
    "monte_carlo_empirical.confidence_interval_upper": "CI Upper (Empirical MC)",
};

function buildLineData(datapoints, label, color) {
    const sorted = [...datapoints].sort((a, b) => new Date(a.timestamp || a.run_id) - new Date(b.timestamp || b.run_id));
    return {
        labels: sorted.map(d => {
            const ts = d.validation_timestamp || d.timestamp;
            return ts ? new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : `#${d.run_id}`;
        }),
        datasets: [{
            label,
            data: sorted.map(d => d.value != null ? Number(d.value) : null),
            borderColor: color,
            backgroundColor: `${color}22`,
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            tension: 0.3,
            fill: false,
            spanGaps: true,
        }],
    };
}

function buildCIChartData(ciData) {
    const sorted = [...ciData].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const labels = sorted.map(d => new Date(d.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" }));
    return {
        labels,
        datasets: [
            {
                label: "CI Upper",
                data: sorted.map(d => d.upper),
                borderColor: "rgba(159,234,232,0.4)",
                backgroundColor: "rgba(159,234,232,0.08)",
                borderDash: [4, 4],
                borderWidth: 1.5,
                pointRadius: 2,
                fill: "+1",
                tension: 0.3,
                spanGaps: true,
            },
            {
                label: "CI Median",
                data: sorted.map(d => d.median),
                borderColor: "#9feae8",
                backgroundColor: "rgba(159,234,232,0.05)",
                borderWidth: 2,
                pointRadius: 3,
                fill: false,
                tension: 0.3,
                spanGaps: true,
            },
            {
                label: "CI Lower",
                data: sorted.map(d => d.lower),
                borderColor: "rgba(159,234,232,0.4)",
                backgroundColor: "rgba(159,234,232,0.08)",
                borderDash: [4, 4],
                borderWidth: 1.5,
                pointRadius: 2,
                fill: false,
                tension: 0.3,
                spanGaps: true,
            },
        ],
    };
}

const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: false,
        },
        tooltip: {
            backgroundColor: "#111",
            borderColor: "rgba(255,255,255,0.12)",
            borderWidth: 1,
            titleColor: "rgba(255,255,255,0.8)",
            bodyColor: "rgba(255,255,255,0.6)",
            callbacks: {
                label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(4) ?? "—"}`,
            },
        },
    },
    scales: {
        x: {
            ticks: { color: "rgba(255,255,255,0.4)", font: { size: 10 }, maxRotation: 0 },
            grid: { color: "rgba(255,255,255,0.05)" },
        },
        y: {
            ticks: { color: "rgba(255,255,255,0.4)", font: { size: 10 } },
            grid: { color: "rgba(255,255,255,0.05)" },
        },
    },
};

const METRIC_COLORS = {
    "historical_backtest.risk_of_ruin":                "#f87171",
    "historical_backtest.win_rate":                    "#4ade80",
    "walk_forward.walk_forward_degradation":           "#fbbf24",
    "walk_forward.overfit_score":                      "#f97316",
    "monte_carlo_empirical.confidence_interval_lower": "#9feae8",
    "monte_carlo_empirical.confidence_interval_upper": "#7dd3fc",
};

export default function TrendsSection() {
    const [strategy, setStrategy] = useState(SUPPORTED_STRATEGIES[0]);
    const [chartData, setChartData] = useState(null);
    const [trendData, setTrendData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeMetric, setActiveMetric] = useState("walk_forward.overfit_score");

    useEffect(() => {
        setLoading(true);
        setError(null);
        Promise.all([fetchChartData(strategy, 30), fetchStrategyTrend(strategy, 30)])
            .then(([cd, td]) => {
                setChartData(cd);
                setTrendData(td);
            })
            .catch((e) => setError(e?.response?.data?.error || "Failed to load trend data"))
            .finally(() => setLoading(false));
    }, [strategy]);

    const trends = trendData?.trends || {};
    const regressionWarnings = trendData?.regression_warnings || [];
    const overallDirection = trendData?.overall_direction;

    const activeTrend = trends[activeMetric];
    const ciData = chartData?.confidence_intervals || [];

    const lineData = activeTrend?.datapoints?.length
        ? buildLineData(activeTrend.datapoints, TREND_METRIC_LABELS[activeMetric] || activeMetric, METRIC_COLORS[activeMetric] || "#9feae8")
        : null;

    const ciChartData = ciData.length ? buildCIChartData(ciData) : null;

    return (
        <div>
            {/* Strategy selector */}
            <div style={{ display: "flex", gap: 8, marginBottom: 20, alignItems: "center" }}>
                {SUPPORTED_STRATEGIES.map(s => (
                    <button
                        key={s}
                        onClick={() => setStrategy(s)}
                        style={{
                            padding: "5px 12px",
                            borderRadius: 6,
                            fontSize: "12px",
                            fontWeight: s === strategy ? 700 : 500,
                            background: s === strategy ? "rgba(159,234,232,0.12)" : "transparent",
                            border: s === strategy ? "1px solid rgba(159,234,232,0.4)" : "1px solid rgba(255,255,255,0.1)",
                            color: s === strategy ? "var(--savvy-accent)" : "var(--savvy-muted)",
                            cursor: "pointer",
                        }}
                    >
                        {STRATEGY_LABELS[s]}
                    </button>
                ))}
                {overallDirection && (
                    <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: "12px", color: "var(--savvy-muted)" }}>
                        Overall: <TrendIndicator direction={overallDirection} showLabel />
                    </div>
                )}
            </div>

            {/* Regression warnings */}
            {regressionWarnings.length > 0 && (
                <div style={{
                    padding: "8px 12px",
                    background: "rgba(248,113,113,0.08)",
                    border: "1px solid rgba(248,113,113,0.3)",
                    borderRadius: 8,
                    marginBottom: 16,
                }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: "#f87171", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                        Regression Warnings
                    </div>
                    {regressionWarnings.map((w, i) => (
                        <div key={i} style={{ fontSize: "12px", color: "rgba(255,255,255,0.7)", marginBottom: 2 }}>↓ {w.message}</div>
                    ))}
                </div>
            )}

            {loading && <div style={{ color: "var(--savvy-muted)", fontSize: "13px", padding: "24px 0", textAlign: "center" }}>Loading trends…</div>}
            {error && <div style={{ color: "#f87171", fontSize: "13px" }}>Error: {error}</div>}

            {!loading && !error && (
                <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 20 }}>
                    {/* Metric selector list */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <div style={{ fontSize: "10px", color: "var(--savvy-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                            Metric
                        </div>
                        {Object.keys(TREND_METRIC_LABELS).map(key => {
                            const t = trends[key];
                            const dir = t?.direction;
                            const isActive = key === activeMetric;
                            return (
                                <button
                                    key={key}
                                    onClick={() => setActiveMetric(key)}
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        padding: "7px 10px",
                                        borderRadius: 6,
                                        fontSize: "11px",
                                        background: isActive ? "rgba(159,234,232,0.1)" : "transparent",
                                        border: isActive ? "1px solid rgba(159,234,232,0.3)" : "1px solid transparent",
                                        color: isActive ? "var(--savvy-text)" : "var(--savvy-muted)",
                                        cursor: "pointer",
                                        textAlign: "left",
                                    }}
                                >
                                    <span>{TREND_METRIC_LABELS[key]}</span>
                                    {dir && <TrendIndicator direction={dir} />}
                                </button>
                            );
                        })}

                        {/* Trend summaries */}
                        {activeTrend?.summary && (
                            <div style={{ marginTop: 16, padding: "10px", background: "var(--savvy-panel)", borderRadius: 8, border: "1px solid var(--savvy-border)" }}>
                                <div style={{ fontSize: "10px", color: "var(--savvy-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                                    Summary
                                </div>
                                {[
                                    ["Earliest", activeTrend.summary.earliest],
                                    ["Latest", activeTrend.summary.latest],
                                    ["Min", activeTrend.summary.min],
                                    ["Max", activeTrend.summary.max],
                                    ["Delta", activeTrend.summary.delta],
                                ].map(([label, val]) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: 4 }}>
                                        <span style={{ color: "var(--savvy-muted)" }}>{label}</span>
                                        <span style={{
                                            color: label === "Delta" && val != null
                                                ? val > 0 ? "#4ade80" : val < 0 ? "#f87171" : "var(--savvy-text)"
                                                : "var(--savvy-text)",
                                            fontWeight: 600,
                                        }}>
                                            {val != null ? (label === "Delta" && val > 0 ? "+" : "") + val.toFixed(4) : "—"}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Charts */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                        {/* Main metric chart */}
                        <div>
                            <div style={{ fontSize: "11px", color: "var(--savvy-muted)", marginBottom: 8 }}>
                                {TREND_METRIC_LABELS[activeMetric]} — Last 30 runs
                            </div>
                            <div style={{ height: 200, position: "relative" }}>
                                {lineData
                                    ? <Line data={lineData} options={chartOptions} />
                                    : <div style={{ color: "var(--savvy-dim)", fontSize: "12px", paddingTop: 80, textAlign: "center" }}>No data available</div>
                                }
                            </div>
                        </div>

                        {/* Confidence interval band chart */}
                        <div>
                            <div style={{ fontSize: "11px", color: "var(--savvy-muted)", marginBottom: 8 }}>
                                Confidence Interval Band (Empirical MC)
                            </div>
                            <div style={{ height: 180, position: "relative" }}>
                                {ciChartData
                                    ? <Line data={ciChartData} options={{ ...chartOptions, plugins: { ...chartOptions.plugins, legend: { display: true, labels: { color: "rgba(255,255,255,0.5)", font: { size: 10 } } } } }} />
                                    : <div style={{ color: "var(--savvy-dim)", fontSize: "12px", paddingTop: 70, textAlign: "center" }}>No confidence interval data</div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
